import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PageProps {
  children: ReactNode;
  className?: string;
}

interface SlotProps {
  children: ReactNode;
  className?: string;
}

function PageRoot({ children, className }: PageProps) {
  return (
    <div className={cn('flex flex-col h-dvh overflow-hidden bg-off-white', className)}>
      {children}
    </div>
  );
}

function PageHeader({ children, className }: SlotProps) {
  return (
    <header className={cn('flex shrink-0 items-center h-auto md:h-23 border-b border-dinnerplate-white', className)}>
      {children}
    </header>
  );
}

const PageBody = forwardRef<HTMLDivElement, SlotProps>(function PageBody(
  { children, className },
  ref,
) {
  // py-8 is baked in so consumers don't have to remember to add it to every
  // inner wrapper — horizontal padding stays per-consumer because list pages
  // use responsive (`px-6 sm:px-8 lg:px-16` matching orders/events mobile) while detail/edit pages use a
  // fixed wide `px-16`.
  return (
    <div ref={ref} className={cn('flex flex-col flex-1 min-h-0 overflow-y-auto py-4 md:py-8', className)}>
      {children}
    </div>
  );
});

export const MOBILE_CONTENT_PADDING = 'px-6 sm:px-8 lg:px-16';

/**
 * Standardised title row for page content.
 * Hidden on mobile because ResponsivePageHeader owns the title there.
 */
export function PageTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('hidden md:flex items-center gap-3 flex-shrink-0', className)}>
      {children}
    </div>
  );
}

function PageFooter({ children, className }: SlotProps) {
  return (
    <footer className={cn('flex-shrink-0 h-auto md:h-23 border-t border-dinnerplate-white', className)}>
      {children}
    </footer>
  );
}

export const Page = Object.assign(PageRoot, {
  Header: PageHeader,
  Body: PageBody,
  Footer: PageFooter,
});
